<template>
  <div>
    <div class="md-layout">
      <div class="md-layout-item md-size-100">
        <CustomChart
          ref="chart"
          :data="functionData"
          @column-update="getInvoices"
        >
          <template slot="actions">
            <div v-if="metaData" class="balance-display">
              <h3>PDV: {{ priceDisplay(metaData.vat) }}</h3>
              <h3>Bez PDV-a: {{ priceDisplay(metaData.withoutVat) }}</h3>
              <h3>
                Zaduženo fakturama: {{ priceDisplay(metaData.totalAmount) }}
              </h3>
            </div>
          </template>
        </CustomChart>
      </div>
      <Invoice
        ref="invoice"
        v-if="showInvoice"
        :id="$route.query.id"
        @saved-invoice="getInvoices"
        @close-invoice="closeInvoice"
      />
      <Payment
        v-else-if="payment !== null"
        :cash="$route.query.filter === 'cash'"
        :client="payment.client"
        :payment="payment"
        :mode="'inPayment'"
        @saved="getInvoices"
        @close="payment = null"
      />
    </div>

    <SearchTable
      ref="table"
      :header="
        $route.query.filter === 'cash'
          ? {
              title: 'Blagajna izlaz',
              icon: 'savings',
              class: 'md-card-header-brand',
            }
          : {
              title: 'Izlazne fakture',
              icon: 'arrow_forward',
              class: 'md-card-header-brand',
            }
      "
      :queriedData="invoices"
      :key="reloading"
      :metaData="metaData"
      :getItems="getInvoices"
      :params="params"
      :sortBy="'invoiceNumber'"
      :newItem="newPayment"
      :openItem="openInvoice"
      :templates="templates"
      :individualFunction="
        !$route.query.filter
          ? [
              {
                function: openAdditionalDocument,
                class: 'md-round',
                icon: 'description',
                condition: hasAdditionalDocument,
              },
              {
                function: sendForVat,
                class: '',
                icon: 'qr_code',
                condition: isNotSentForVat,
              },
              {
                function: cancelInvoice,
                class: 'md-danger',
                icon: 'qr_code_scanner',
                condition: isSentForVat,
              },
              {
                function: downloadInvoice,
                class: 'md-round md-success',
                icon: 'download',
              },
            ]
          : [
              {
                function: openAdditionalDocument,
                class: 'md-round',
                icon: 'description',
                condition: hasAdditionalDocument,
              },
              {
                function: downloadInvoice,
                class: 'md-round md-success',
                icon: 'download',
              },
            ]
      "
      :forEachSelected="[
        {
          icon: 'sync',
          label: 'Konvertuj',
          class: 'md-warning',
          function: convert,
        },
        {
          icon: 'delete',
          label: 'Briši',
          class: 'md-danger',
          function: deleteInvoices,
        },
      ]"
    />
  </div>
</template>

<script>
import services from '@/api/services';
import api from '@/api/api.js';
import Swal from 'sweetalert2';
import SearchTable from '@/components/SearchTable.vue';
import Invoice from '@/components/Invoice.vue';
import CustomChart from '@/components/Cards/CustomChart.vue';
import Payment from '@/components/Payment.vue';
import { getCurrentYearStart, cases } from '@/utils/helpers';

export default {
  components: {
    SearchTable,
    CustomChart,
    Payment,
    Invoice,
  },
  data() {
    return {
      params: {
        invoiceNumber: null,
        clientName: '',
        totalPrice: [null, null],
        vat: ['all', '0.0', '0.07', '0.21'],
        offerIds: '',
        iic: ['all', 'new', 'cancelled', 'draft', 'sent'],
        creator: '',
        created: [getCurrentYearStart(), null],
      },
      hideChart: false,
      metaData: null,
      reloading: 0,
      invoices: [],
      functionData: {
        labels: [],
        series: [],
      },
      payment: null,
      showInvoice: false,
      templates: [
        {
          name: 'Fiskalizovano',
          params: {
            iic: ['sent'],
          },
        },
      ],
    };
  },
  methods: {
    isNotSentForVat(item) {
      return !item.sentForVat;
    },
    isSentForVat(item) {
      return item.sentForVat;
    },
    hasAdditionalDocument(item) {
      return !!item.additionalDocumentUrl;
    },
    openAdditionalDocument(item) {
      window.open(item.additionalDocumentUrl, '_blank');
    },
    getInvoices(printTable = false) {
      const offset = this.metaData ? this.$refs.table.from : 0;
      const limit = this.metaData ? this.$refs.table.pagination.perPage : 75;
      const sortBy = this.metaData ? this.$refs.table.currentSort : null;
      const sortOrder = this.metaData
        ? this.$refs.table.currentSortOrder
        : null;
      const columns = this.metaData ? this.$refs.chart.columns : 12;
      const mode = this.metaData ? this.$refs.chart.mode : 'Bar';
      return api
        .get(services.invoices.getInvoices, {
          getInvoices: true,
          params: this.params,
          offset,
          limit,
          sortBy,
          filter: this.$route.query.filter ? this.$route.query.filter : null,
          sortOrder,
          columns,
          mode,
          printTable,
        })
        .then(data => {
          this.invoices = data.data;
          this.metaData = data.metaData;
          if (data.metaData.labels)
            this.functionData.labels = data.metaData.labels;
          if (data.metaData.data) this.functionData.series = data.metaData.data;
          if (data.metaData.legend)
            this.functionData.legend = data.metaData.legend;
          if (this.$refs.chart) this.$refs.chart.updateChart();
        })
        .catch(error => {
          this.alertError(error);
        });
    },
    downloadInvoice(item) {
      api
        .post(services.invoices.getInvoiceBody, {
          downloadInvoice: true,
          id: item.id,
          asPdf: true,
        })
        .then(data => {
          //Create a Blob from the PDF Stream
          var byteCharacters = atob(data);
          var byteNumbers = new Array(byteCharacters.length);
          for (var i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          var byteArray = new Uint8Array(byteNumbers);
          var blob = new Blob([byteArray], { type: 'application/pdf' });

          const fileURL = URL.createObjectURL(blob);
          //Open the URL on new Window
          window.open(fileURL);
        })
        .catch(error => {
          this.alertError(error);
        });
    },
    async sendForVat(item) {
      return Swal.fire({
        title: 'Fiskalizuj?',
        showCancelButton: true,
        confirmButtonClass: 'md-button md-info btn-fill',
        cancelButtonClass: 'md-button btn-fill',
        confirmButtonText: 'Fiskalizuj',
        cancelButtonText: 'Otkaži',
        buttonsStyling: false,
      }).then(({ value }) => {
        if (!value) return;

        return api
          .post(services.invoices.sendForVat, { id: item.id })
          .then(data => {
            this.getInvoices();
            if (data.url) window.open(data.url, '_blank');
          })
          .catch(error => {
            this.alertError(error);
          });
      });
    },
    async cancelInvoice(item) {
      const { url } = await api.get(services.invoices.getQrCodeUrl, {
        id: item.id,
      });

      return Swal.fire({
        title: 'Faktura je fiskalizovana',
        html: `<a href="${url}" target="_blank">Provjerite račun preko linka</a>`,
        showCancelButton: true,
        confirmButtonClass: 'md-button md-danger btn-fill',
        cancelButtonClass: 'md-button btn-fill',
        confirmButtonText: 'Storniraj',
        cancelButtonText: 'Otkaži',
        buttonsStyling: false,
      }).then(({ value }) => {
        if (!value) return;
        return Swal.fire({
          title: 'Storniraj fakturu?',
          showCancelButton: true,
          confirmButtonClass: `md-button md-danger btn-fill`,
          cancelButtonClass: 'md-button btn-fill',
          confirmButtonText: 'Storniraj',
          cancelButtonText: 'Otkaži',
          buttonsStyling: false,
        }).then(({ value }) => {
          if (!value) return;

          api
            .post(services.invoices.cancelInvoice, { id: item.id })
            .then(data => {
              this.getInvoices();
              if (data.url) window.open(data.url, '_blank');
            })
            .catch(error => {
              this.alertError(error);
            });
        });
      });
    },
    openInvoice(item) {
      if (this.showInvoice) this.showInvoice = false;
      if (this.payment) this.payment = null;

      let id;
      if (
        typeof item.id === 'string' &&
        (id = item.id.match(/payment_(\d*)/))
      ) {
        id = id[1];
        api
          .get(services.payments.getPayment, {
            getPayment: true,
            paymentId: id,
          })
          .then(data => {
            this.payment = data;
          })
          .catch(error => {
            this.alertError(error);
          });
      } else {
        if (typeof item.id === 'string')
          id = item.id.match(/paid_invoice_(\d*)/);
        if (!id) id = item.id;
        else id = id[1];

        const query = {};

        if (this.$route.query) Object.assign(query, this.$route.query, { id });
        this.$router.push({ path: 'invoices', query });

        this.$nextTick().then(() => {
          // Add the component back in
          this.showInvoice = true;
        });
      }
    },
    closeInvoice() {
      this.showInvoice = false;
      const query = {};
      if (this.$route.query) Object.assign(query, this.$route.query);
      if (query.id) delete query.id;
      this.$router.push({ path: 'invoices', query });
    },
    newPayment() {
      if (this.showInvoice) this.showInvoice = false;
      if (this.payment) this.payment = null;

      if (this.$route.query.filter === 'cash') {
        return Swal.fire({
          text: `Upiši fakturu ili uplatu?`,
          type: 'warning',
          showCancelButton: true,
          confirmButtonClass: 'md-button btn-fill',
          cancelButtonClass: 'md-button btn-fill',
          confirmButtonText: 'Fakturu',
          cancelButtonText: 'Uplatu',
          buttonsStyling: false,
        }).then(data => {
          if (data.value) {
            this.newInvoiceSub();
          } else {
            this.payment = {
              clientId: null,
              dateSigned: null,
              amount: null,
              direction: 'out',
              comment: null,
            };
          }
        });
      } else {
        this.newInvoiceSub();
      }
    },
    newInvoiceSub() {
      const query = {};
      if (this.$route.query) Object.assign(query, this.$route.query, { id: 0 });
      this.$router.push({ path: 'invoices', query });

      this.$nextTick().then(() => {
        // Add the component back in
        this.showInvoice = true;
      });
    },
    deleteInvoices(items) {
      const ids = items.map(el => el.id);
      return Swal.fire({
        title: 'Obriši fakture?',
        text: `Da li ste sigurni da želite da obrišete ${ids.length} ${cases(
          ids.length,
          'fakturu',
          'fakture',
          'faktura',
        )}?`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonClass: 'md-button md-danger btn-fill',
        cancelButtonClass: 'md-button btn-fill',
        confirmButtonText: 'Obriši!',
        cancelButtonText: 'Otkaži',
        buttonsStyling: false,
      }).then(data => {
        if (data.value) {
          (() => {
            if (ids.length >= 5) {
              return Swal.fire({
                title: 'Obriši fakture?',
                text: `Označili ste ${ids.length} ${cases(
                  ids.length,
                  'fakturu',
                  'fakture',
                  'faktura',
                )} za brisanje. Potvrdite još jednom da želite da nastavite.`,
                type: 'warning',
                showCancelButton: true,
                cancelButtonClass: 'md-button btn-fill',
                confirmButtonClass: 'md-button md-danger btn-fill',
                confirmButtonText: 'Obriši!',
                cancelButtonText: 'Otkaži',
                buttonsStyling: false,
              });
            } else {
              return Promise.resolve(data);
            }
          })().then(data => {
            if (data.value) {
              api
                .post(services.invoices.deleteInvoices, {
                  deleteInvoices: true,
                  ids,
                })
                .then(() => {
                  this.getInvoices();
                  this.$refs.table.removeChecked();
                })
                .catch(error => {
                  this.alertError(error);
                });
            }
          });
        }
      });
    },
    convert(items) {
      const ids = items.map(el => el.id);
      return Swal.fire({
        title:
          this.$route.query.filter === 'cash'
            ? 'Konvertuj gotovinu?'
            : 'Konvertuj fakture?',
        text:
          this.$route.query.filter === 'cash'
            ? `Da li ste sigurni da želite da konvertujete ${
                ids.length
              } ${cases(
                ids.length,
                'gotovinu',
                'gotovine',
                'gotovine',
              )} u fakture?`
            : `Da li ste sigurni da želite da konvertujete ${
                ids.length
              } ${cases(
                ids.length,
                'fakturu',
                'fakture',
                'faktura',
              )} u gotovinu?`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonClass: 'md-button md-warning btn-fill',
        cancelButtonClass: 'md-button btn-fill',
        confirmButtonText: 'Konvertuj!',
        cancelButtonText: 'Otkaži',
        buttonsStyling: false,
      }).then(data => {
        if (data.value) {
          (() => {
            if (ids.length >= 5) {
              return Swal.fire({
                title:
                  this.$route.query.filter === 'cash'
                    ? 'Konvertuj gotovinu?'
                    : 'Konvertuj fakture?',
                text:
                  this.$route.query.filter === 'cash'
                    ? `Označili ste ${ids.length} ${cases(
                        ids.length,
                        'gotovinu',
                        'gotovine',
                        'gotovine',
                      )} za konverziju. Potvrdite još jednom da želite da nastavite.`
                    : `Označili ste ${ids.length} ${cases(
                        ids.length,
                        'fakturu',
                        'fakture',
                        'faktura',
                      )} za konverziju. Potvrdite još jednom da želite da nastavite.`,
                type: 'warning',
                showCancelButton: true,
                cancelButtonClass: 'md-button btn-fill',
                confirmButtonClass: 'md-button md-warning btn-fill',
                confirmButtonText: 'Konvertuj!',
                cancelButtonText: 'Otkaži',
                buttonsStyling: false,
              });
            } else {
              return Promise.resolve(data);
            }
          })().then(data => {
            if (data.value) {
              api
                .post(services.offers.convertInvoices, {
                  convertInvoices: true,
                  ids,
                })
                .then(() => {
                  this.getInvoices();
                  this.$refs.table.removeChecked();
                })
                .catch(error => {
                  this.alertError(error);
                });
            }
          });
        }
      });
    },
  },
  mounted() {
    this.getInvoices().then(() => {
      if (this.$route.query.id !== undefined) this.showInvoice = true;
    });
  },
  watch: {
    $route() {
      this.payment = null;
      if (this.$route.query.id === undefined) this.showInvoice = false;
      this.getInvoices();
    },
    'metaData.columnParams': {
      handler(to, from) {
        if (to && (!from || to.length !== from.length)) this.reloading++;
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.balance-display {
  display: flex;
  justify-content: space-between;
  gap: 3rem;
  margin: 0 1rem;
}
</style>
